import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../services/apiClient';
import TermsContent from './TermsContent';

export default function TermsModal({ isOpen, onClose, onAccepted, mode = 'accept' }) {
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation('common');
  const isViewMode = mode === 'view';

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setAccepted(false);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!accepted) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/profile/accept-terms', {});
      setSuccess(true);
      onAccepted?.(response.terms_accepted_at);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError(t('terms.error.submit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="div" className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('terms.title')}
                  </h3>
                  {(!success || isViewMode) && (
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  )}
                </Dialog.Title>

                {isViewMode ? (
                  <>
                    <TermsContent />
                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        {t('terms.close')}
                      </button>
                    </div>
                  </>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <TermsContent />

                    <label className="mt-4 flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        disabled={success || isSubmitting}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
                      />
                      <span className="text-sm text-gray-700">{t('terms.acceptCheckbox')}</span>
                    </label>

                    {error && (
                      <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
                        {t('terms.success')}
                      </div>
                    )}

                    {!success && (
                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={isSubmitting}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                          {t('terms.cancel')}
                        </button>
                        <button
                          type="submit"
                          disabled={!accepted || isSubmitting}
                          className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                            ${accepted && !isSubmitting ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                            focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                        >
                          {isSubmitting ? t('terms.submitting') : t('terms.submit')}
                        </button>
                      </div>
                    )}
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
